import React from "react";
import { MessageCircle } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useRequireAuth } from "@/components/auth/useRequireAuth";
import { useT } from "@/lib/LanguageContext";
import { tenantHref } from "@/lib/tenantPath";

/**
 * FloatingChatWidget — the public site's door to the visitor's conversation with the
 * practice (modules/messaging). The Contact section now captures a CRM lead instead
 * (ContactForm), so this button is the one way left to write to the practice.
 *
 * The thread belongs to a signed-in identity (`messaging/service._require_client`), so
 * the press goes through the same `useRequireAuth()` gate as every transactional public
 * surface, and lands on the client account (`/compte`), which renders the thread.
 * `authDialog` must be rendered here: the hook only opens it, and without it the gate's
 * promise would never settle.
 *
 * Hidden on the account page itself, which already shows the thread.
 */
export default function FloatingChatWidget() {
  const t = useT("public");
  const { ensureAuthed, authDialog } = useRequireAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const portalPath = tenantHref("/compte");

  if (pathname === portalPath || pathname.startsWith(`${portalPath}/`)) return null;

  const handleClick = async () => {
    // Dismissed: `useRequireAuth` already tells the visitor, nothing more to say here.
    const user = await ensureAuthed();
    if (user) navigate(portalPath);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        data-testid="floating-chat-widget"
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-eh-ink text-white shadow-lg transition-transform hover:scale-105"
        aria-label={t("portal.messages")}
        title={t("portal.messages")}
      >
        <MessageCircle size={24} aria-hidden="true" />
      </button>
      {authDialog}
    </>
  );
}
